import { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Shell } from "./Shell";
import { FadeUp } from "./Sections";

const HEAR_OPTIONS = [
  "Referral",
  "Web search",
  "Event or conference",
  "HARC newsletter",
  "Social media",
  "News or media coverage",
  "Other",
];

const FIELD =
  "w-full border border-[#004778]/30 bg-white px-4 py-3 text-sm text-[#022A42] placeholder:text-[#5C6B72]/70 focus:outline-none focus:ring-2 focus:ring-[#004778]";
const LABEL =
  "mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-[#022A42]";

export default function Conversation() {
  const [submitted, setSubmitted] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Shell>
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-24" data-testid="conversation-page">
        <FadeUp>
          <p className="mb-5 text-xs font-bold uppercase tracking-[0.18em] text-[#004778]">
            Start a Conversation
          </p>
          <h1 className="harc-heading max-w-3xl text-4xl font-medium leading-[1.1] tracking-tight text-[#004778] md:text-6xl">
            Tell us what you're trying to solve
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#5C6B72] md:text-lg md:font-light">
            Whether you're navigating an emerging issue, building a new initiative, or looking for
            the right partners and expertise, HARC can help turn insight into action.
          </p>
        </FadeUp>

        <div className="mt-14 grid gap-12 md:grid-cols-12">
          <FadeUp delay={0.1} className="md:col-span-7">
            {submitted ? (
              <div
                className="border-l-4 border-[#7EBC37] bg-[#004778]/5 p-8"
                data-testid="conversation-confirmation"
                role="status"
              >
                <CheckCircle2 size={28} className="text-[#004778]" aria-hidden="true" />
                <h2 className="harc-heading mt-4 text-2xl font-medium text-[#004778]">
                  Thank you. Your enquiry is on its way.
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-[#5C6B72]">
                  It goes directly to the right HARC team based on your challenge, and someone will
                  follow up with you.
                </p>
                <p className="mt-4 text-xs italic text-[#5C6B72]">
                  Mockup note: in production this form is built in Gravity Forms with conditional
                  routing to the appropriate HARC contact, and entries stay exportable for a future
                  CRM.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="conversation-form" className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cf-name" className={LABEL}>
                      Name *
                    </label>
                    <input id="cf-name" required className={FIELD} data-testid="form-name" autoComplete="name" />
                  </div>
                  <div>
                    <label htmlFor="cf-org" className={LABEL}>
                      Organization
                    </label>
                    <input id="cf-org" className={FIELD} data-testid="form-organization" autoComplete="organization" />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className={LABEL}>
                      Email *
                    </label>
                    <input id="cf-email" type="email" required className={FIELD} data-testid="form-email" autoComplete="email" />
                  </div>
                  <div>
                    <label htmlFor="cf-phone" className={LABEL}>
                      Phone
                    </label>
                    <input id="cf-phone" type="tel" className={FIELD} data-testid="form-phone" autoComplete="tel" />
                  </div>
                </div>
                <div>
                  <label htmlFor="cf-challenge" className={LABEL}>
                    What challenge are you trying to solve? *
                  </label>
                  <textarea
                    id="cf-challenge"
                    required
                    rows={5}
                    className={FIELD}
                    data-testid="form-challenge"
                  />
                </div>
                <div>
                  <label htmlFor="cf-hear" className={LABEL}>
                    How did you hear about HARC?
                  </label>
                  <select id="cf-hear" className={FIELD} data-testid="form-hear-about" defaultValue="">
                    <option value="" disabled>
                      Select an option
                    </option>
                    {HEAR_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  data-testid="form-submit"
                  className="inline-flex items-center gap-3 rounded-full bg-[#FFA100] px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-[#022A42] transition-colors hover:bg-[#D98A00]"
                >
                  Send to HARC <ArrowRight size={16} aria-hidden="true" />
                </button>
              </form>
            )}
          </FadeUp>

          <FadeUp delay={0.2} className="md:col-span-4 md:col-start-9">
            <aside className="space-y-8">
              <div className="border border-[#004778]/25 bg-white p-7" data-testid="routing-sidebar">
                <h2 className="harc-heading text-xl font-medium text-[#004778]">
                  What happens next
                </h2>
                <ol className="mt-4 space-y-4">
                  {[
                    "Your enquiry goes directly to the right HARC team based on your challenge.",
                    "A HARC team member follows up to learn more.",
                    "Together we scope whether and how HARC can help.",
                  ].map((s, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-[#5C6B72]">
                      <span className="harc-heading font-bold text-[#FFA100]">{i + 1}.</span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
              <div className="border border-[#004778]/25 bg-white p-7" data-testid="contact-sidebar">
                <h2 className="harc-heading text-xl font-medium text-[#004778]">Reach us directly</h2>
                <div className="mt-4 space-y-3 text-sm text-[#5C6B72]">
                  <p>[HARC CONTENT REQUIRED: office address]</p>
                  <p>[HARC CONTENT REQUIRED: phone]</p>
                  <p>[HARC CONTENT REQUIRED: general email]</p>
                </div>
              </div>
            </aside>
          </FadeUp>
        </div>
      </div>
    </Shell>
  );
}
